import { useState } from 'react';

import { ColumnConfigurationRenderAs } from '@april9au/stack9-sdk';
import * as ui from '@april9au/stack9-ui';
import { Empty, message } from 'antd';
import type { Key } from 'antd/lib/table/interface';

import { bulkCancelBookings } from '../../utils/bulkCancelBookings';

const S9PageSection = ui.S9PageSection.default;
const S9Button = ui.S9Button.default;
const S9Space = ui.S9Space.default;
const S9Popconfirm = ui.S9Popconfirm.default;
const { Grid } = ui;

interface BookingsSectionProps {
  eventId?: number;
}

export function BookingsSection({ eventId }: BookingsSectionProps) {
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  if (!eventId) {
    return (
      <S9PageSection size="sm">
        <Empty
          description="Save the event first to view bookings"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </S9PageSection>
    );
  }

  const handleBulkCancel = async () => {
    setIsLoading(true);
    try {
      const result = await bulkCancelBookings({
        bookingIds: selectedRowKeys as number[],
        cancellationReason: 'Admin bulk cancellation',
        forceCancel: true,
      });

      message.success(
        `Successfully cancelled ${
          result.successCount || selectedRowKeys.length
        } booking(s)`,
      );
      setSelectedRowKeys([]);
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      message.error('Failed to cancel bookings. Please try again.');
      console.error('Bulk cancel error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const rowSelection = {
    type: 'checkbox' as const,
    selectedRowKeys,
    onChange: (keys: Key[]) => {
      setSelectedRowKeys(keys);
    },
    getCheckboxProps: (record: { _workflow_outcome?: string }) => ({
      disabled: record._workflow_outcome === 'Cancelled',
    }),
  };

  return (
    <S9PageSection size="sm">
      {selectedRowKeys.length > 0 && (
        <S9Space style={{ marginBottom: 16 }}>
          <span>
            <strong>{selectedRowKeys.length}</strong> booking(s) selected
          </span>
          <S9Popconfirm
            title={`Cancel ${selectedRowKeys.length} booking(s)? This action cannot be undone.`}
            onConfirm={handleBulkCancel}
            okText="Yes, Cancel All"
            cancelText="No"
          >
            <S9Button label="Bulk Cancel" danger loading={isLoading} />
          </S9Popconfirm>
          <S9Button
            label="Clear Selection"
            onClick={() => setSelectedRowKeys([])}
            disabled={isLoading}
          />
        </S9Space>
      )}
      <Grid
        key={refreshKey}
        name="event_bookings_list"
        searchQueryKey="getbookingsbyeventid"
        queryVars={{
          id: eventId,
          page: 0,
          limit: 50,
          filters: JSON.stringify({}) as never,
        }}
        showTotal
        rowSelection={rowSelection}
        columnsConfiguration={[
          {
            label: 'Booking Reference',
            field: 'booking_reference',
            value: '{{booking_reference}}',
            renderAs: ColumnConfigurationRenderAs.Text,
            options: {
              linkProp: '/crm/booking-detail/{{id}}',
            },
          },
          {
            label: 'User',
            field: 'user.full_name',
            value: '{{user.full_name}}',
            renderAs: ColumnConfigurationRenderAs.Text,
          },
          {
            label: 'Email',
            field: 'user.email',
            value: '{{user.email}}',
            renderAs: ColumnConfigurationRenderAs.Text,
          },
          {
            label: 'Phone',
            field: 'user.phone',
            value: '{{user.phone}}',
            renderAs: ColumnConfigurationRenderAs.Text,
          },
          {
            label: 'Appointment Time',
            field: 'slot.start_dt',
            value: '{{slot.start_dt}}',
            renderAs: ColumnConfigurationRenderAs.Date,
            options: {
              noTime: false,
            },
          },
          {
            label: 'Booking Date',
            field: 'booking_dt',
            value: '{{booking_dt}}',
            renderAs: ColumnConfigurationRenderAs.Date,
            options: {
              noTime: false,
            },
          },
          {
            label: 'Status',
            field: '_workflow_current_step',
            value: '{{_workflow_current_step}}',
            renderAs: ColumnConfigurationRenderAs.WorkflowResult,
          },
          {
            label: 'Position',
            field: 'position',
            value: '{{position}}',
            renderAs: ColumnConfigurationRenderAs.Text,
          },
          {
            label: 'Department',
            field: 'department.name',
            value: '{{department.name}}',
            renderAs: ColumnConfigurationRenderAs.Text,
          },
          {
            label: 'Consent',
            field: 'consent_given',
            value: '{{consent_given}}',
            renderAs: ColumnConfigurationRenderAs.EnumTags,
            options: {
              enumColor: {
                Yes: 'green',
                No: 'gray',
              },
            },
          },
          {
            label: 'Waiver',
            field: 'waiver_signed',
            value: '{{waiver_signed}}',
            renderAs: ColumnConfigurationRenderAs.EnumTags,
            options: {
              enumColor: {
                Yes: 'green',
                No: 'gray',
              },
            },
          },
        ]}
      />
    </S9PageSection>
  );
}
